import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Edit2, Eye, CheckCircle, AlertCircle, Copy, X
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach } from '../../lib/auth';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface LeadPage {
  id: string;
  slug: string;
  headline: string;
  description: string;
  cta_text: string;
  image_url: string | null;
  is_active: boolean;
  booking_url: string | null;
  assessment_code: string | null;
  discover_active: boolean;
  conversation_active: boolean;
  discover_headline: string | null;
  conversation_headline: string | null;
}

export function LeadPageEditor() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [page, setPage] = useState<LeadPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState({
    slug: '',
    headline: 'Try My AI Coach',
    description: 'Experience a personalized coaching session powered by AI. Get instant insights and guidance tailored to your needs.',
    cta_text: 'Get Started Free',
    image_url: '',
    is_active: true,
    booking_url: '',
    assessment_code: '',
    discover_active: false,
    conversation_active: false,
    discover_headline: '',
    conversation_headline: '',
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) { navigate('/login'); return; }

        const { data: coach } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coach) { navigate('/login'); return; }
        setCoachId(coach.id);

        const { data: existingPage } = await supabase
          .from('coach_lead_pages')
          .select('*')
          .eq('coach_id', coach.id)
          .maybeSingle();

        if (existingPage) {
          setPage(existingPage);
          setForm({
            slug: existingPage.slug,
            headline: existingPage.headline,
            description: existingPage.description,
            cta_text: existingPage.cta_text,
            image_url: existingPage.image_url || '',
            is_active: existingPage.is_active,
            booking_url: existingPage.booking_url || '',
            assessment_code: existingPage.assessment_code || '',
            discover_active: existingPage.discover_active || false,
            conversation_active: existingPage.conversation_active || false,
            discover_headline: existingPage.discover_headline || '',
            conversation_headline: existingPage.conversation_headline || '',
          });
        } else {
          // Generate a default slug from user email
          const slug = user.email?.split('@')[0]?.replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'my-coach';
          setForm(f => ({ ...f, slug }));
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load lead page settings');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!coachId) return;
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const slug = form.slug.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');

      if (page) {
        const { error: err } = await supabase
          .from('coach_lead_pages')
          .update({
            slug,
            headline: form.headline,
            description: form.description,
            cta_text: form.cta_text,
            image_url: form.image_url || null,
            is_active: form.is_active,
            booking_url: form.booking_url || null,
            assessment_code: form.assessment_code || null,
            discover_active: form.discover_active,
            conversation_active: form.conversation_active,
            discover_headline: form.discover_headline || null,
            conversation_headline: form.conversation_headline || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', page.id);

        if (err) throw err;
      } else {
        const { data: newPage, error: err } = await supabase
          .from('coach_lead_pages')
          .insert({
            coach_id: coachId,
            slug,
            headline: form.headline,
            description: form.description,
            cta_text: form.cta_text,
            image_url: form.image_url || null,
            is_active: form.is_active,
            booking_url: form.booking_url || null,
            assessment_code: form.assessment_code || null,
            discover_active: form.discover_active,
            conversation_active: form.conversation_active,
            discover_headline: form.discover_headline || null,
            conversation_headline: form.conversation_headline || null,
          })
          .select()
          .single();

        if (err) throw err;
        setPage(newPage);
      }

      setForm(f => ({ ...f, slug }));
      setSuccess('Lead page saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      if (err.code === '23505') {
        setError('This URL slug is already taken. Please choose a different one.');
      } else {
        setError(err.message || 'Failed to save lead page');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const origin = window.location.origin;
  const pageUrl = `${origin}/try/${form.slug}`;
  const discoverUrl = `${origin}/discover/${form.slug}`;
  const conversationUrl = `${origin}/coach-conversation/${form.slug}`;

  const [copiedWhich, setCopiedWhich] = useState<string | null>(null);

  const copyUrl = async (url: string, key: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedWhich(key);
    setTimeout(() => setCopiedWhich(null), 2000);
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading lead page settings..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-teal-500 rounded-xl shadow-soft">
                <Edit2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">Lead Pages</h1>
                <p className="freud-text-sm text-chocolate-500">
                  Your shareable pages that attract and capture leads
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {error && (
          <div className="bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
          </div>
        )}
        {success && (
          <div className="bg-sage-100 border border-sage-200 text-sage-700 px-4 py-3 rounded-xl flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            {success}
          </div>
        )}

        {/* Your 3 shareable pages */}
        {page && (
          <div className="freud-card-elevated p-6">
            <h2 className="text-base font-bold text-chocolate-900 mb-1">Your Shareable Links</h2>
            <p className="text-sm text-chocolate-500 mb-5">Share these links anywhere — social media, email, your website, or direct message.</p>
            <div className="space-y-3">
              {[
                {
                  key: 'try',
                  label: 'Try My AI Coach',
                  description: 'Simple opt-in page. Visitor enters their name and email, then gets access to your AI coach.',
                  url: pageUrl,
                  active: form.is_active,
                  color: 'bg-teal-50 border-teal-200',
                  badge: 'bg-teal-100 text-teal-700',
                },
                {
                  key: 'discover',
                  label: 'Discover My Profile',
                  description: 'Long-form page explaining the free NLP OS profile. Best for people who need more context before signing up.',
                  url: discoverUrl,
                  active: form.discover_active,
                  color: 'bg-sage-50 border-sage-200',
                  badge: 'bg-sage-100 text-sage-700',
                },
                {
                  key: 'conversation',
                  label: 'Start an AI Conversation',
                  description: 'Landing page with a live AI coaching chat. Visitor enters name and email, then has a short conversation with your AI coach.',
                  url: conversationUrl,
                  active: form.conversation_active,
                  color: 'bg-amber-50 border-amber-200',
                  badge: 'bg-amber-100 text-amber-700',
                },
              ].map(({ key, label, description, url, active, color, badge }) => (
                <div key={key} className={`rounded-xl border p-4 ${color}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-chocolate-900">{label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${active ? badge : 'bg-gray-100 text-gray-500'}`}>
                          {active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-xs text-chocolate-500 mb-2 leading-relaxed">{description}</p>
                      <div className="flex items-center gap-1.5 bg-white/70 rounded-lg px-3 py-1.5 border border-white/50">
                        <span className="text-xs text-chocolate-500 truncate flex-1 font-mono">{url}</span>
                        <button
                          type="button"
                          onClick={() => copyUrl(url, key)}
                          className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 font-medium flex-shrink-0"
                        >
                          {copiedWhich === key ? <CheckCircle className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {copiedWhich === key ? 'Copied!' : 'Copy'}
                        </button>
                        <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-chocolate-400 hover:text-chocolate-700 flex-shrink-0 ml-1">
                          <Eye className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 bg-cream-100 rounded-xl p-4 text-sm text-chocolate-600 leading-relaxed">
              <p className="font-semibold text-chocolate-800 mb-1">How it works</p>
              <ol className="space-y-1 list-decimal list-inside text-xs">
                <li>Someone visits your link and enters their name and email.</li>
                <li>They are automatically added to your <strong>Sales Pipeline</strong> as a new lead.</li>
                <li>Go to <strong>Sales Pipeline</strong> to see them, read their first message, add notes, and schedule a follow-up.</li>
                <li>Move them through your pipeline stages as you contact, qualify, and convert them.</li>
              </ol>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Editor */}
          <div className="freud-card-elevated p-6">
            <h2 className="text-lg font-bold text-chocolate-900 mb-6">Page Settings</h2>
            <form onSubmit={handleSave} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">URL Slug</label>
                <p className="text-xs text-chocolate-400 mb-1.5">This is your unique identifier used across all three pages.</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-chocolate-400">/try/</span>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    required
                    className="freud-input flex-1"
                    placeholder="my-coaching"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Headline</label>
                <input
                  type="text"
                  value={form.headline}
                  onChange={(e) => setForm({ ...form, headline: e.target.value })}
                  required
                  className="freud-input"
                  placeholder="Try My AI Coach"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  required
                  rows={4}
                  className="freud-input resize-none"
                  placeholder="Tell visitors what they'll experience..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Button Text</label>
                <input
                  type="text"
                  value={form.cta_text}
                  onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
                  required
                  className="freud-input"
                  placeholder="Get Started Free"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Image URL (optional)</label>
                <input
                  type="url"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="freud-input"
                  placeholder="https://..."
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-cream-100 rounded-xl">
                <span className="text-sm font-medium text-chocolate-700">Try Page Active</span>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, is_active: !form.is_active })}
                  className="flex-shrink-0"
                >
                  <div className={`relative w-11 h-6 rounded-full transition-colors ${form.is_active ? 'bg-sage-500' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${form.is_active ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                  </div>
                </button>
              </div>

              {/* Divider */}
              <div className="pt-4 border-t border-cream-200">
                <h3 className="text-sm font-bold text-chocolate-900 mb-1">Discover & Conversation Pages</h3>
                <p className="text-xs text-chocolate-500 mb-4">
                  These settings control your /discover and /coach-conversation pages. Toggle each one on when you're ready to share it.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Booking URL</label>
                <input
                  type="url"
                  value={form.booking_url}
                  onChange={(e) => setForm({ ...form, booking_url: e.target.value })}
                  className="freud-input"
                  placeholder="https://calendly.com/your-link"
                />
                <p className="text-xs text-chocolate-400 mt-1">The "Book a Call" button on the conversation page links here.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Assessment Code</label>
                <input
                  type="text"
                  value={form.assessment_code}
                  onChange={(e) => setForm({ ...form, assessment_code: e.target.value })}
                  className="freud-input"
                  placeholder="e.g. COACH-ABC123"
                />
                <p className="text-xs text-chocolate-400 mt-1">Links "Take the Free Profile" buttons directly to your assessment.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Discover Page Headline (optional)</label>
                <input
                  type="text"
                  value={form.discover_headline}
                  onChange={(e) => setForm({ ...form, discover_headline: e.target.value })}
                  className="freud-input"
                  placeholder="Custom headline for your /discover page"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Conversation Page Headline (optional)</label>
                <input
                  type="text"
                  value={form.conversation_headline}
                  onChange={(e) => setForm({ ...form, conversation_headline: e.target.value })}
                  className="freud-input"
                  placeholder="Custom headline for your /coach-conversation page"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-cream-100 rounded-xl">
                <span className="text-sm font-medium text-chocolate-700">Discover Page Active</span>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, discover_active: !form.discover_active })}
                  className="flex-shrink-0"
                >
                  <div className={`relative w-11 h-6 rounded-full transition-colors ${form.discover_active ? 'bg-sage-500' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${form.discover_active ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                  </div>
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-cream-100 rounded-xl">
                <span className="text-sm font-medium text-chocolate-700">Conversation Page Active</span>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, conversation_active: !form.conversation_active })}
                  className="flex-shrink-0"
                >
                  <div className={`relative w-11 h-6 rounded-full transition-colors ${form.conversation_active ? 'bg-sage-500' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${form.conversation_active ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                  </div>
                </button>
              </div>

              <button type="submit" className="freud-btn-primary w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Page'}
              </button>
            </form>
          </div>

          {/* Preview */}
          <div>
            <h2 className="text-lg font-bold text-chocolate-900 mb-4">Live Preview</h2>
            <div className="border border-cream-300 rounded-xl overflow-hidden shadow-lg">
              <div className="bg-gradient-to-br from-teal-600 to-teal-800 p-8 text-center">
                {form.image_url && (
                  <img src={form.image_url} alt="" className="w-20 h-20 rounded-full mx-auto mb-4 object-cover border-4 border-white/20" />
                )}
                <h3 className="text-2xl font-bold text-white mb-3">{form.headline || 'Your Headline'}</h3>
                <p className="text-teal-100 text-sm leading-relaxed max-w-sm mx-auto">
                  {form.description || 'Your description will appear here...'}
                </p>
              </div>
              <div className="bg-white p-6">
                <div className="space-y-3 max-w-xs mx-auto">
                  <div className="h-10 bg-cream-100 rounded-lg border border-cream-300 flex items-center px-3">
                    <span className="text-xs text-chocolate-400">Full Name</span>
                  </div>
                  <div className="h-10 bg-cream-100 rounded-lg border border-cream-300 flex items-center px-3">
                    <span className="text-xs text-chocolate-400">Email Address</span>
                  </div>
                  <button className="w-full py-2.5 bg-teal-600 text-white rounded-lg text-sm font-semibold">
                    {form.cta_text || 'Get Started'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
